import { useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Code, CopyButton, Group, Modal, Select,
  Stack, Switch, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { useDisclosure } from '@mantine/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import {
  IconCheck, IconCopy, IconEdit, IconKey, IconPlus, IconShieldLock,
} from '@tabler/icons-react'
import {
  ROLES, usuarioEntrada, type Usuario, type UsuarioCreado, type UsuarioEntrada,
} from '@serkat/shared'
import { ErrorApi, api } from '../../api/cliente'
import { useSesion } from '../auth/useSesion'
import { vacio } from '../../compartido/formato'

/** Qué puede hacer cada rol, en los términos del proceso real. */
const DESCRIPCION_ROL: Record<string, string> = {
  PROCURA: 'Registra proveedores y emite requerimientos y órdenes',
  DIRECTOR: 'Aprueba, observa o anula documentos',
  GERENTE: 'Aprueba, observa o anula documentos',
  ADMIN: 'Todo lo anterior, más la administración de cuentas y empresas',
  CONSULTA: 'Solo lectura: consulta y descarga, no modifica nada',
}

const VACIO: UsuarioEntrada = {
  nombres: '', apellidos: '', correo: '', cargo: '', dni: '', rol: 'PROCURA',
}

export function UsuariosPagina() {
  const { usuario: yo } = useSesion()
  const qc = useQueryClient()
  const [editando, setEditando] = useState<Usuario | undefined>()
  const [abierto, modal] = useDisclosure(false)
  /** Se muestra una sola vez; después ya no es recuperable. */
  const [credencial, setCredencial] = useState<UsuarioCreado | undefined>()

  const { data: usuarios } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api.get<Usuario[]>('/usuarios'),
  })

  const form = useForm<UsuarioEntrada>({
    initialValues: VACIO,
    validate: zodResolver(usuarioEntrada),
  })

  const alFallar = (titulo: string) => (e: unknown) =>
    notifications.show({
      color: 'red',
      title: titulo,
      message: e instanceof ErrorApi ? e.message : 'Error inesperado',
    })

  const refrescar = () => void qc.invalidateQueries({ queryKey: ['usuarios'] })

  // Crear y editar son operaciones distintas —una devuelve credencial y la otra
  // no—, así que no comparten mutación: unificarlas obligaba a un cast que
  // mentía sobre el tipo de la respuesta.
  const crear = useMutation({
    mutationFn: (v: UsuarioEntrada) => api.post<UsuarioCreado>('/usuarios', v),
    onSuccess: (res) => {
      refrescar()
      modal.close()
      setCredencial(res)
    },
    onError: alFallar('No se pudo crear la cuenta'),
  })

  const editar = useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: UsuarioEntrada }) =>
      api.patch<Usuario>(`/usuarios/${id}`, datos),
    onSuccess: () => {
      refrescar()
      modal.close()
      notifications.show({ color: 'teal', message: 'Cuenta actualizada' })
    },
    onError: alFallar('No se pudo guardar'),
  })

  const cambiarEstado = useMutation({
    mutationFn: (u: Usuario) => api.patch<Usuario>(`/usuarios/${u.id}`, { activo: !u.activo }),
    onSuccess: (u) => {
      refrescar()
      notifications.show({
        color: 'teal',
        message: u.activo
          ? 'Cuenta reactivada'
          : 'Cuenta desactivada: sus sesiones abiertas se cerraron',
      })
    },
    onError: alFallar('No se pudo cambiar el estado'),
  })

  const reestablecer = useMutation({
    mutationFn: (u: Usuario) => api.post<UsuarioCreado>(`/usuarios/${u.id}/reestablecer-password`, {}),
    onSuccess: (res) => {
      refrescar()
      setCredencial(res)
    },
    onError: alFallar('No se pudo restablecer'),
  })

  function abrir(u?: Usuario) {
    setEditando(u)
    form.setValues(
      u
        ? {
            nombres: u.nombres, apellidos: u.apellidos, correo: u.correo,
            cargo: u.cargo ?? '', dni: u.dni ?? '', rol: u.rol,
          }
        : VACIO,
    )
    modal.open()
  }

  const sinEstrenar = (usuarios ?? []).filter((u) => u.activo && u.debeCambiarPassword)

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Usuarios</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => abrir()}>
          Nueva cuenta
        </Button>
      </Group>

      <Alert color="blue" variant="light" icon={<IconShieldLock size={16} />}>
        Cada persona debe tener su propia cuenta. El historial de cada documento
        guarda quién lo emitió y quién lo aprobó; con cuentas compartidas esa
        trazabilidad deja de servir como respaldo.
      </Alert>

      {sinEstrenar.length > 0 && (
        <Alert color="yellow" variant="light">
          {sinEstrenar.length === 1
            ? 'Una cuenta sigue con su contraseña temporal'
            : `${sinEstrenar.length} cuentas siguen con su contraseña temporal`}
          . Hasta que la cambien, no pueden usar el sistema.
        </Alert>
      )}

      <Table.ScrollContainer minWidth={860}>
        <Table withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nombre</Table.Th>
              <Table.Th>Correo</Table.Th>
              <Table.Th>Rol</Table.Th>
              <Table.Th>Cargo</Table.Th>
              <Table.Th w={150}>Estado</Table.Th>
              <Table.Th w={100} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(usuarios ?? []).map((u) => (
              <Table.Tr key={u.id}>
                <Table.Td>
                  {u.apellidos}, {u.nombres}
                  {u.id === yo?.id && (
                    <Badge ml="xs" size="xs" variant="light" color="blue">usted</Badge>
                  )}
                </Table.Td>
                <Table.Td>{u.correo}</Table.Td>
                <Table.Td>
                  <Tooltip label={DESCRIPCION_ROL[u.rol] ?? ''} multiline w={260}>
                    <Badge variant="light" size="sm">{u.rolNombre}</Badge>
                  </Tooltip>
                </Table.Td>
                <Table.Td>{vacio(u.cargo)}</Table.Td>
                <Table.Td>
                  <Badge color={u.activo ? 'teal' : 'gray'} variant="light" size="sm">
                    {u.activo ? 'Activa' : 'Inactiva'}
                  </Badge>
                  {u.activo && u.debeCambiarPassword && (
                    <Badge ml={6} color="yellow" variant="light" size="sm">temporal</Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Tooltip label="Editar">
                      <ActionIcon variant="subtle" onClick={() => abrir(u)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Generar nueva contraseña temporal">
                      <ActionIcon
                        variant="subtle"
                        loading={reestablecer.isPending}
                        onClick={() => reestablecer.mutate(u)}
                      >
                        <IconKey size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Modal
        opened={abierto}
        onClose={modal.close}
        title={editando ? 'Editar cuenta' : 'Nueva cuenta'}
        size="lg"
      >
        <form
          onSubmit={form.onSubmit((v) =>
            editando ? editar.mutate({ id: editando.id, datos: v }) : crear.mutate(v),
          )}
        >
          <Stack gap="sm">
            <Group grow align="flex-start">
              <TextInput label="Nombres" withAsterisk {...form.getInputProps('nombres')} />
              <TextInput label="Apellidos" withAsterisk {...form.getInputProps('apellidos')} />
            </Group>
            <Group grow align="flex-start">
              <TextInput
                label="Correo"
                withAsterisk
                description={editando ? undefined : 'Con este correo iniciará sesión'}
                {...form.getInputProps('correo')}
              />
              <TextInput label="DNI" maxLength={8} placeholder="Opcional" {...form.getInputProps('dni')} />
            </Group>
            <TextInput label="Cargo" placeholder="Opcional" {...form.getInputProps('cargo')} />
            <Select
              label="Rol"
              withAsterisk
              allowDeselect={false}
              data={ROLES.map((r) => ({ value: r, label: DESCRIPCION_ROL[r] ? r : r }))}
              renderOption={({ option }) => (
                <Stack gap={0}>
                  <Text size="sm">{option.value}</Text>
                  <Text size="xs" c="dimmed">{DESCRIPCION_ROL[option.value]}</Text>
                </Stack>
              )}
              description={DESCRIPCION_ROL[form.values.rol]}
              {...form.getInputProps('rol')}
            />

            {editando && (
              <Switch
                label="Cuenta activa"
                description="Al desactivarla, sus sesiones abiertas se cierran de inmediato"
                checked={editando.activo}
                onChange={() => cambiarEstado.mutate(editando)}
              />
            )}

            {!editando && (
              <Alert color="gray" variant="light">
                No se define una contraseña aquí: el sistema genera una temporal
                que deberá entregar a la persona. Ella elige la definitiva al
                entrar, y nadie más la conoce.
              </Alert>
            )}

            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={modal.close}>Cancelar</Button>
              <Button type="submit" loading={crear.isPending || editar.isPending}>
                {editando ? 'Guardar' : 'Crear cuenta'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={!!credencial}
        onClose={() => setCredencial(undefined)}
        title="Contraseña temporal"
        size="md"
      >
        <Stack gap="sm">
          <Text size="sm">
            Entréguesela a <b>{credencial?.usuario.nombres} {credencial?.usuario.apellidos}</b>.
            El sistema le pedirá cambiarla al entrar.
          </Text>

          <Group justify="space-between" wrap="nowrap">
            <Code fz="lg" px="sm" py={6}>{credencial?.passwordTemporal}</Code>
            <CopyButton value={credencial?.passwordTemporal ?? ''}>
              {({ copied, copy }) => (
                <Button
                  variant="light"
                  color={copied ? 'teal' : 'blue'}
                  leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  onClick={copy}
                >
                  {copied ? 'Copiada' : 'Copiar'}
                </Button>
              )}
            </CopyButton>
          </Group>

          <Alert color="yellow" variant="light">
            No vuelve a mostrarse. El sistema guarda solo su huella cifrada, no la
            contraseña. Si se pierde, genere otra con la llave de la lista.
          </Alert>

          <Group justify="flex-end">
            <Button onClick={() => setCredencial(undefined)}>Entendido</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
